/**
 * Pharmacy partners — the directory.
 *
 * This is the merge of two screens that were showing the same collection from
 * two angles: "Pharmacy Partner" (a paged table with a verify switch) and
 * "Pharmacy Details" (an unpaged table with a map). Keeping them apart meant
 * the map silently loaded every pharmacy in the estate on page open, the two
 * tables disagreed about which columns mattered, and a search had to be typed
 * twice to be sure of the answer.
 *
 * One filter set now drives both views: the table pages against it and the map
 * plots every pin matching it, so switching view never changes the answer.
 *
 * Both old URLs still resolve here — /admin/saleForce and
 * /admin/pharmacy-details — because they are bookmarked and the pharmacy
 * console's back button points at the second one.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  Divider,
  Collapse,
  TextField,
  MenuItem,
  Badge,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  MdRefresh,
  MdFileDownload,
  MdTune,
  MdMap,
  MdViewList,
  MdVerticalSplit,
} from "react-icons/md";

import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";

import { Surface, Kpi, DataTable, FilterBar, ConfirmDialog } from "components/ui";
import { T, tint } from "theme/tokens";

import * as api from "./api";
import buildColumns from "./columns";
import PharmacyMap from "./PharmacyMap";
import EditPharmacyDialog from "./EditPharmacyDialog";

// ── filter definitions ──────────────────────────────────────────────────────

const YES_NO = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const BANK_STATE_OPTIONS = [
  { value: "verified", label: "Verified" },
  { value: "pending", label: "Verification pending" },
  { value: "review", label: "Needs review" },
  { value: "failed", label: "Failed" },
  { value: "unverified", label: "Never verified" },
];

const POS_OPTIONS = [
  { value: "connected", label: "Connected" },
  { value: "none", label: "Not connected" },
  { value: "Marg", label: "Marg" },
  { value: "Busy", label: "Busy" },
  { value: "GoFrugal", label: "GoFrugal" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name", label: "Name A–Z" },
  { value: "pincode", label: "Pincode" },
];

const EMPTY_FILTERS = {
  search: "",
  verified: "",
  takingOrders: "",
  hasBank: "",
  bankStatus: "",
  hasLocation: "",
  otpVerified: "",
  posSync: "",
  hasGst: "",
  hasLicense: "",
  pincode: "",
  joinedFrom: "",
  joinedTo: "",
  deleted: "",
  sortBy: "newest",
};

// Which filters live behind "More filters". The badge counts these, so the
// button says how much is hidden rather than making someone open it to check.
const ADVANCED_KEYS = [
  "bankStatus",
  "hasLocation",
  "otpVerified",
  "posSync",
  "hasGst",
  "hasLicense",
  "pincode",
  "joinedFrom",
  "joinedTo",
  "deleted",
];

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: T.surface, fontSize: "0.85rem" },
};

/**
 * Columns and density only.
 *
 * Not the stock GridToolbar: its filter and export act on the loaded page
 * rather than the query, so on a server-paged table they would quietly answer
 * a different question than the one asked. Filtering and export are handled
 * above, against the server.
 */
function TableToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 1, pb: 0.5, gap: 0.5 }}>
      <GridToolbarColumnsButton sx={{ color: T.muted, textTransform: "none" }} />
      <GridToolbarDensitySelector sx={{ color: T.muted, textTransform: "none" }} />
    </GridToolbarContainer>
  );
}

// The columns worth the horizontal space by default. The rest stay one click
// away in the Columns menu rather than pushing Actions off the screen.
const DEFAULT_HIDDEN_COLUMNS = {
  businessTiming: false,
  dateOfRegistration: false,
};

// ── page ────────────────────────────────────────────────────────────────────

export default function PharmacyPartners() {
  const navigate = useNavigate();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // The search box is typed into; `filters.search` is what has been committed
  // to the server. Keeping them apart is what makes debouncing invisible.
  const [searchText, setSearchText] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Split by default: the map above the table is how the estate is read at a
  // glance — which pharmacies are live, which only ever registered.
  const [view, setView] = useState("split");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [selection, setSelection] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState({ kind: null });
  // { message, severity } — a failure must not be announced in the same
  // green banner as a success.
  const [toast, setToast] = useState(null);
  const notify = (message, severity = "success") => setToast({ message, severity });

  const debounce = useRef(null);

  // Server params, rebuilt whenever a committed filter changes. The map reads
  // the same object, minus paging, so the two views can never disagree.
  const query = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) params[key] = value;
    });
    return params;
  }, [filters]);

  const queryKey = JSON.stringify(query);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listPharmacies({
        ...JSON.parse(queryKey),
        page: pagination.page + 1,
        limit: pagination.pageSize,
      });
      setRows(data?.pharmacies || []);
      setTotal(data?.pagination?.total || 0);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load pharmacies."
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [queryKey, pagination.page, pagination.pageSize]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      // Search-scoped only — see the endpoint's comment: the tiles are filter
      // shortcuts, so their counts must not move when you click one. That is
      // also why this depends on the search term rather than the whole query:
      // a facet count over the collection is not cheap enough to re-run every
      // time a dropdown changes something it does not read.
      const data = await api.listSummary({ search: filters.search });
      setSummary(data?.summary || null);
    } catch {
      // The tiles degrade to dashes; the table reports its own failure.
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters.search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const refresh = () => {
    load();
    loadSummary();
  };

  // ── filter helpers ──

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPagination((p) => ({ ...p, page: 0 }));
    setSelection([]);
  };

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setFilter("search", value), 400);
  };

  const reset = () => {
    if (debounce.current) clearTimeout(debounce.current);
    setSearchText("");
    setFilters(EMPTY_FILTERS);
    setPagination({ page: 0, pageSize: 25 });
    setSelection([]);
  };

  const advancedCount = ADVANCED_KEYS.filter((key) => filters[key]).length;

  /** A summary tile doubles as a filter. Clicking the active one clears it. */
  const applyTile = (patch) => {
    const alreadyOn = Object.entries(patch).every(
      ([key, value]) => filters[key] === value
    );
    setFilters((f) => ({
      ...f,
      ...EMPTY_FILTERS,
      search: f.search,
      sortBy: f.sortBy,
      ...(alreadyOn ? {} : patch),
    }));
    setPagination((p) => ({ ...p, page: 0 }));
    setSelection([]);
    if (!alreadyOn && ADVANCED_KEYS.some((key) => key in patch)) setShowAdvanced(true);
  };

  const tileActive = (patch) =>
    Object.entries(patch).every(([key, value]) => filters[key] === value);

  // ── row actions ──

  const toggleVerified = async (row, next) => {
    // Rejecting a pharmacy stops its orders, and the backend refuses it
    // without a reason — so switching off opens the dialog, switching on
    // does not.
    if (!next) {
      setDialog({ kind: "unverify", row });
      return;
    }
    setBusyId(row.pharmacyId);
    try {
      await api.setVerification(row.pharmacyId, true, "");
      setRows((list) =>
        list.map((r) =>
          r.pharmacyId === row.pharmacyId ? { ...r, verifiedPharamacy: true } : r
        )
      );
      notify(`${row.bussinessName || "Pharmacy"} approved.`);
      loadSummary();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Could not approve that pharmacy.",
        "error"
      );
    } finally {
      setBusyId(null);
    }
  };

  const columns = useMemo(
    () =>
      buildColumns({
        onOpenConsole: (row) => navigate(`/admin/pharmacy/${row.pharmacyId}`),
        onOpenInventory: (row) =>
          navigate(`/admin/pharmacy-inventory/${row.pharmacyId}`),
        onEdit: (row) => setDialog({ kind: "edit", row }),
        onDelete: (row) => setDialog({ kind: "delete", row }),
        onRestore: (row) => setDialog({ kind: "restore", row }),
        onToggleVerified: toggleVerified,
        busyId,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, busyId]
  );

  // Selection is keyed on _id because `pharmacyId` is null on a handful of
  // legacy documents and a duplicate row id breaks the grid. The bulk API
  // wants pharmacyIds, so they are resolved back here.
  const selectedIds = useMemo(
    () =>
      rows
        .filter((row) => selection.includes(row._id))
        .map((row) => row.pharmacyId)
        .filter(Boolean),
    [rows, selection]
  );

  const runBulk = async (action, reason) => {
    const data = await api.bulkUpdate(selectedIds, action, reason);
    setSelection([]);
    refresh();
    const skipped = data?.skipped?.length || 0;
    notify(
      `${data?.count || 0} pharmac${data?.count === 1 ? "y" : "ies"} updated` +
        (skipped ? ` · ${skipped} skipped` : "") +
        "."
    );
  };

  const exportCsv = () => {
    window.open(api.exportUrl(query), "_blank", "noopener");
  };

  // ── render ──

  const showTable = view === "table" || view === "split";
  const showMap = view === "map" || view === "split";

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Surface sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1.5}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: 800,
                color: T.primary,
                letterSpacing: "-0.02em",
              }}
            >
              Pharmacy Partners
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: T.muted, mt: 0.3, maxWidth: 640 }}>
              Every registered pharmacy, its approval and trading state, and where it
              is on the map. Approving one starts routing customer orders to it.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, next) => next && setView(next)}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  border: `1px solid ${T.border}`,
                  color: T.muted,
                  px: 1.2,
                  gap: 0.6,
                  fontSize: "0.78rem",
                },
                "& .Mui-selected": {
                  bgcolor: `${tint(T.primary, 0.1)} !important`,
                  color: `${T.primary} !important`,
                },
              }}
            >
              <ToggleButton value="table">
                <MdViewList size={16} /> Table
              </ToggleButton>
              <ToggleButton value="split">
                <MdVerticalSplit size={16} /> Split
              </ToggleButton>
              <ToggleButton value="map">
                <MdMap size={16} /> Map
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              size="small"
              startIcon={<MdFileDownload />}
              onClick={exportCsv}
              sx={{ textTransform: "none", color: T.secondary }}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              startIcon={<MdRefresh />}
              onClick={refresh}
              sx={{ textTransform: "none", color: T.primary }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Surface>

      {/* ── Summary tiles, which are also the fastest filters ──────────── */}
      <Surface sx={{ mb: 2 }}>
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} flexWrap="wrap">
          {[
            {
              label: "Pharmacies",
              value: summary?.active,
              sub: `${summary?.pincodes ?? 0} pincodes covered`,
              tone: T.primary,
              patch: {},
            },
            {
              label: "Verified",
              value: summary?.verified,
              sub: "approved to trade",
              tone: T.success,
              patch: { verified: "true" },
            },
            {
              label: "Pending",
              value: summary?.pending,
              sub: "awaiting approval",
              tone: T.warning,
              patch: { verified: "false" },
            },
            {
              label: "Taking orders",
              value: summary?.takingOrders,
              sub: "open right now",
              tone: T.info,
              patch: { takingOrders: "true" },
            },
            {
              label: "Bank review",
              value: summary?.bankReview,
              sub: "name match unclear",
              tone: summary?.bankReview ? T.error : T.muted,
              patch: { bankStatus: "review" },
            },
            {
              label: "Not mapped",
              value: summary?.unmapped,
              sub: "no location set",
              tone: T.warning,
              patch: { hasLocation: "false" },
            },
            {
              label: "Deleted",
              value: summary?.deleted,
              sub: "soft-deleted",
              tone: T.muted,
              patch: { deleted: "true" },
            },
          ].map((tile) => {
            const active = Object.keys(tile.patch).length > 0 && tileActive(tile.patch);
            return (
              <Box
                key={tile.label}
                onClick={() => applyTile(tile.patch)}
                sx={{
                  flex: "1 1 150px",
                  cursor: "pointer",
                  position: "relative",
                  transition: "background-color 0.15s ease",
                  bgcolor: active ? tint(tile.tone, 0.08) : "transparent",
                  "&:hover": { bgcolor: tint(tile.tone, active ? 0.12 : 0.05) },
                  // The active tile is underlined in its own tone, so it is
                  // legible without relying on the fill alone.
                  "&::after": active
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 3,
                        bgcolor: tile.tone,
                      }
                    : undefined,
                }}
              >
                <Kpi
                  label={tile.label}
                  value={summary ? (tile.value ?? 0).toLocaleString("en-IN") : "—"}
                  sub={tile.sub}
                  tone={tile.tone}
                  loading={summaryLoading}
                />
              </Box>
            );
          })}
        </Stack>
      </Surface>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <Surface sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <FilterBar
          search={searchText}
          onSearchChange={onSearchChange}
          searchPlaceholder="Name, owner, phone, email, Medstown ID, GST, pincode…"
          selects={[
            {
              key: "verified",
              label: "Approval",
              value: filters.verified,
              minWidth: 145,
              options: [
                { value: "true", label: "Verified" },
                { value: "false", label: "Pending" },
              ],
              onChange: (v) => setFilter("verified", v),
            },
            {
              key: "takingOrders",
              label: "Taking orders",
              value: filters.takingOrders,
              minWidth: 150,
              options: YES_NO,
              onChange: (v) => setFilter("takingOrders", v),
            },
            {
              key: "hasBank",
              label: "Bank on file",
              value: filters.hasBank,
              minWidth: 145,
              options: YES_NO,
              onChange: (v) => setFilter("hasBank", v),
            },
            {
              key: "sortBy",
              label: "Sort",
              value: filters.sortBy,
              minWidth: 150,
              options: SORT_OPTIONS,
              onChange: (v) => setFilter("sortBy", v),
            },
          ]}
          onRefresh={refresh}
          onReset={reset}
        >
          <Badge badgeContent={advancedCount} color="primary" overlap="rectangular">
            <Button
              size="small"
              variant={showAdvanced ? "contained" : "outlined"}
              disableElevation
              startIcon={<MdTune />}
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                whiteSpace: "nowrap",
                ...(showAdvanced
                  ? { bgcolor: T.primary, "&:hover": { bgcolor: "#013838" } }
                  : { borderColor: T.border, color: T.muted }),
              }}
            >
              More filters
            </Button>
          </Badge>
        </FilterBar>

        <Collapse in={showAdvanced} unmountOnExit>
          <Divider sx={{ mb: 1.8 }} />
          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
            {[
              {
                key: "bankStatus",
                label: "Bank verification",
                options: BANK_STATE_OPTIONS,
                width: 190,
              },
              { key: "hasLocation", label: "On the map", options: YES_NO, width: 145 },
              { key: "posSync", label: "POS sync", options: POS_OPTIONS, width: 160 },
              { key: "otpVerified", label: "OTP verified", options: YES_NO, width: 150 },
              { key: "hasGst", label: "Has GST", options: YES_NO, width: 130 },
              { key: "hasLicense", label: "Has licence", options: YES_NO, width: 145 },
              {
                key: "deleted",
                label: "Deleted",
                options: [
                  { value: "true", label: "Only deleted" },
                  { value: "any", label: "Include deleted" },
                ],
                width: 165,
                allLabel: "Hide deleted",
              },
            ].map((select) => (
              <TextField
                key={select.key}
                select
                size="small"
                label={select.label}
                value={filters[select.key]}
                onChange={(e) => setFilter(select.key, e.target.value)}
                sx={{ ...fieldSx, minWidth: select.width }}
              >
                <MenuItem value="">{select.allLabel || "All"}</MenuItem>
                {select.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}

            <TextField
              size="small"
              label="Pincode"
              value={filters.pincode}
              onChange={(e) => setFilter("pincode", e.target.value)}
              sx={{ ...fieldSx, minWidth: 130 }}
            />

            {/* Onboarding date, taken from the record's own creation time.
                `dateOfRegistration` is a free-form string with more than one
                format in it, so a range over it would drop rows silently. */}
            <TextField
              size="small"
              type="date"
              label="Joined from"
              value={filters.joinedFrom}
              onChange={(e) => setFilter("joinedFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fieldSx, minWidth: 155 }}
            />
            <TextField
              size="small"
              type="date"
              label="Joined to"
              value={filters.joinedTo}
              onChange={(e) => setFilter("joinedTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fieldSx, minWidth: 155 }}
            />
          </Stack>
        </Collapse>
      </Surface>

      {/* ── Bulk action bar ────────────────────────────────────────────── */}
      <Collapse in={selectedIds.length > 0} unmountOnExit>
        <Surface
          sx={{
            p: 1.5,
            mb: 2,
            borderColor: tint(T.primary, 0.35),
            bgcolor: tint(T.primary, 0.05),
          }}
        >
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography sx={{ fontWeight: 700, color: T.primary, fontSize: "0.88rem", mr: 1 }}>
              {selectedIds.length} selected
            </Typography>
            {[
              { action: "verify", label: "Approve", tone: T.success },
              { action: "unverify", label: "Un-approve", tone: T.error },
              { action: "resumeOrders", label: "Resume orders", tone: T.info },
              { action: "pauseOrders", label: "Pause orders", tone: T.warning },
            ].map((item) => (
              <Button
                key={item.action}
                size="small"
                variant="outlined"
                onClick={() => setDialog({ kind: "bulk", action: item.action, label: item.label })}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: "0.78rem",
                  color: item.tone,
                  borderColor: tint(item.tone, 0.4),
                  "&:hover": { borderColor: item.tone, bgcolor: tint(item.tone, 0.06) },
                }}
              >
                {item.label}
              </Button>
            ))}
            <Button
              size="small"
              onClick={() => setSelection([])}
              sx={{ textTransform: "none", color: T.muted, ml: "auto" }}
            >
              Clear selection
            </Button>
          </Stack>
        </Surface>
      </Collapse>

      {/* ── Map ────────────────────────────────────────────────────────── */}
      {showMap && (
        <Box sx={{ mb: showTable ? 2 : 0 }}>
          <PharmacyMap
            filters={query}
            height={view === "map" ? 620 : 400}
            onOpenConsole={(id) => navigate(`/admin/pharmacy/${id}`)}
          />
        </Box>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {showTable && (
        <Surface sx={{ p: { xs: 1, md: 1.5 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, pb: 1 }}
          >
            <Typography sx={{ fontSize: "0.78rem", color: T.muted, fontWeight: 600 }}>
              {loading
                ? "Loading…"
                : `${total.toLocaleString("en-IN")} pharmac${total === 1 ? "y" : "ies"} match`}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.subtle }}>
              Click a row to open its console
            </Typography>
          </Stack>

          <DataTable
            rows={rows}
            columns={columns}
            loading={loading}
            error={error}
            onRetry={load}
            emptyMessage="No pharmacies match these filters."
            rowCount={total}
            paginationModel={pagination}
            onPaginationModelChange={(next) => {
              setPagination(next);
              setSelection([]);
            }}
            getRowId={(row) => row._id}
            // The grid's checkbox cell only stops propagation on the space
            // key, not on click — without this, ticking a box to build a bulk
            // selection navigates straight to that pharmacy's console instead.
            onRowClick={({ row }, event) => {
              if (event?.target?.closest?.('[data-field="__check__"]')) return;
              navigate(`/admin/pharmacy/${row.pharmacyId}`);
            }}
            checkboxSelection
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            slots={{ toolbar: TableToolbar }}
            initialState={{ columns: { columnVisibilityModel: DEFAULT_HIDDEN_COLUMNS } }}
            height={view === "split" ? 540 : 680}
          />
        </Surface>
      )}

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      <EditPharmacyDialog
        open={dialog.kind === "edit"}
        pharmacy={dialog.row}
        onClose={() => setDialog({ kind: null })}
        onSaved={(message) => {
          notify(message);
          load();
        }}
      />

      <ConfirmDialog
        open={dialog.kind === "unverify"}
        title="Un-approve this pharmacy?"
        description={`${
          dialog.row?.bussinessName || "This pharmacy"
        } will stop receiving orders and be hidden from customers.`}
        confirmLabel="Un-approve"
        destructive
        requireReason
        onConfirm={async (reason) => {
          await api.setVerification(dialog.row.pharmacyId, false, reason);
          setRows((list) =>
            list.map((r) =>
              r.pharmacyId === dialog.row.pharmacyId
                ? { ...r, verifiedPharamacy: false }
                : r
            )
          );
          notify("Pharmacy un-approved.");
          loadSummary();
        }}
        onClose={() => setDialog({ kind: null })}
      />

      <ConfirmDialog
        open={dialog.kind === "delete"}
        title="Delete this pharmacy?"
        description={`${
          dialog.row?.bussinessName || "This pharmacy"
        } will stop trading and can no longer sign in. Orders and invoices are kept — they have statutory retention periods — and the record can be restored.`}
        confirmLabel="Delete"
        destructive
        requireReason
        onConfirm={async (reason) => {
          await api.softDeletePharmacy(dialog.row.pharmacyId, reason);
          notify("Pharmacy deleted.");
          refresh();
        }}
        onClose={() => setDialog({ kind: null })}
      />

      <ConfirmDialog
        open={dialog.kind === "restore"}
        title="Restore this pharmacy?"
        description="Its login is restored. It stays un-approved and paused until someone turns those back on."
        confirmLabel="Restore"
        requireReason
        onConfirm={async (reason) => {
          await api.restorePharmacy(dialog.row.pharmacyId, reason);
          notify("Pharmacy restored.");
          refresh();
        }}
        onClose={() => setDialog({ kind: null })}
      />

      <ConfirmDialog
        open={dialog.kind === "bulk"}
        title={`${dialog.label} ${selectedIds.length} pharmac${
          selectedIds.length === 1 ? "y" : "ies"
        }?`}
        description={
          dialog.action === "verify"
            ? "They will start receiving customer orders."
            : dialog.action === "unverify"
            ? "They will stop receiving orders and be hidden from customers."
            : dialog.action === "resumeOrders"
            ? "New orders will be routed to them again."
            : "They stay verified, but new orders will go elsewhere."
        }
        confirmLabel={dialog.label}
        destructive={["unverify", "pauseOrders"].includes(dialog.action)}
        // The backend requires one for the switches that turn something off,
        // and it is the only record of why a whole batch changed at once.
        requireReason={["unverify", "pauseOrders"].includes(dialog.action)}
        onConfirm={(reason) => runBulk(dialog.action, reason)}
        onClose={() => setDialog({ kind: null })}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.severity === "error" ? 6000 : 3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast?.severity || "success"}
          variant="filled"
          onClose={() => setToast(null)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
